import {
    Avatar,
    Box,
    Container,
    FormControl,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import HomeAppBar from "../../components/HomeAppBar/HomeAppBar";
import MuiTable from "../../components/MuiTable/MuiTable";
import {
    formatDateTime,
    httpRequest,
    onRequestError,
} from "../../helper/global";
import { LOCAL_STORAGE_KEY, STATUS_LIST } from "../../helper/constant";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

export default function Users({ theme, user, permissionConfig }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const containerStyle = { ...theme.container };
    containerStyle.p = "0px !important";
    containerStyle.m = "0px !important";

    const [tableCache, setTableCache] = useState(null);
    const [ready, setReady] = useState(false);

    const [roles, setRoles] = useState([]);
    const [updatedRows, setUpdatedRows] = useState([]);

    useEffect(() => {
        setReady(true);
    }, []);

    useEffect(() => {
        if (!permissionConfig || !permissionConfig.userRoles) {
            return;
        }

        const keys = Object.keys(permissionConfig.userRoles);

        const roles = [];

        keys.forEach((key) => {
            roles.push({
                text: permissionConfig.userRoles[key],
                value: permissionConfig.userRoles[key],
            });
        });

        setRoles(roles);
    }, [permissionConfig.userRoles]);

    const handleRoleChange = async (row, role) => {
        row.role = role;
        setUpdatedRows([row]);
        try {
            const result = await httpRequest(
                `user/update/${row.id}`,
                { role: role },
                "POST",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                }
            );

            if (result === false) {
                return;
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleStatusChange = async (row, status) => {
        row.status = status;
        setUpdatedRows([row]);
        try {
            const result = await httpRequest(
                `user/update/${row.id}`,
                { status: status },
                "POST",
                (error) => {
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                }
            );

            if (result === false) {
                return;
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const columns = [
        {
            id: "name",
            label: "Name",
            sort: true,
            visible: true,
            hasAvatar: true,
            formatter: (value, row, index) => {
                return (
                    <Box display={"flex"} alignItems={"center"}>
                        <Avatar
                            alt={value}
                            src={row.avatar ?? value}
                            sx={{ mr: 1 }}
                        />
                        {value}
                    </Box>
                );
            },
        },
        {
            id: "status",
            label: "Status",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                return (
                    <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                        <Select
                            value={value ?? ""}
                            onChange={(event) => {
                                const value = event.target.value;
                                handleStatusChange(row, value);
                            }}
                            displayEmpty
                            size="small"
                            inputProps={{
                                "aria-label": "Select Status",
                            }}
                        >
                            {STATUS_LIST.map((item, index) => (
                                <MenuItem
                                    key={`status-${index}`}
                                    value={item.value}
                                >
                                    {item.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            id: "role",
            label: "User Role",
            sort: true,
            visible: true,
            formatter: (value, row, index) => {
                return (
                    <FormControl variant="outlined" sx={{ minWidth: 120 }}>
                        <Select
                            value={value ?? ""}
                            onChange={(event) => {
                                const value = event.target.value;
                                handleRoleChange(row, value);
                            }}
                            displayEmpty
                            size="small"
                            inputProps={{
                                "aria-label": "Select Time Range",
                            }}
                        >
                            {roles.map((item, index) => (
                                <MenuItem
                                    key={`roles-${index}`}
                                    value={item.value}
                                >
                                    {item.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            },
        },
        {
            id: "lastLogin",
            label: "Last Login",
            sort: false,
            visible: true,
            formatter: (value, row, index) => {
                return formatDateTime(value, "MMM D, YYYY h:mm a", false);
            },
        },
        {
            id: "emailAddress",
            label: "Email",
            sort: false,
            visible: true,
        },
    ];

    const actions = [];

    if (!user || !ready) {
        return;
    }

    return (
        <Container sx={containerStyle}>
            <HomeAppBar
                theme={theme}
                selectedPage="users"
                user={user}
                permissionConfig={permissionConfig}
            />

            <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Typography variant="h4">Users</Typography>

                <Box sx={{ mt: 3 }}>
                    <MuiTable
                        theme={theme}
                        id={"id"}
                        actions={actions}
                        endPoint={"user/list"}
                        headCells={columns}
                        rowsPerPageArray={[100, 200, 500]}
                        updatedRows={updatedRows}
                        CACHE_KEY={LOCAL_STORAGE_KEY.usersCache}
                    />
                </Box>
            </Box>
        </Container>
    );
}
